/**
 * Constantes de Configuração
 * Centralização de valores mágicos e configurações do sistema
 */

// ==================== ESTADOS DO CHAT ====================
export const CHAT_STATES = {
  BOT_ACTIVE: 'BOT_ATIVO',
  WAITING_HUMAN: 'AGUARDANDO_HUMANO',
  HUMAN_SERVICE: 'EM_ATENDIMENTO_HUMANO',
  FINISHED: 'FINALIZADO',
} as const;

export const CHAT_STATE_LABELS: Record<string, string> = {
  [CHAT_STATES.BOT_ACTIVE]: 'Bot Ativo',
  [CHAT_STATES.WAITING_HUMAN]: 'Aguardando Atendimento',
  [CHAT_STATES.HUMAN_SERVICE]: 'Em Atendimento Humano',
  [CHAT_STATES.FINISHED]: 'Finalizado',
};

// ==================== LIMITES E TIMEOUTS ====================
export const LIMITS = {
  // Paginação
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,

  // Chat
  MAX_MESSAGE_LENGTH: 1000,
  MAX_CHAT_HISTORY: 50,

  // Arquivos
  MAX_FILENAME_LENGTH: 255,
  MAX_FILE_SIZE_MB: 10,

  // Rate Limiting
  MAX_LOGIN_ATTEMPTS: 5,
  LOGIN_TIMEOUT_MINUTES: 15,
  MAX_API_CALLS_PER_MINUTE: 60,

  // Timeouts
  API_TIMEOUT_MS: 30000, // 30 segundos
  WEBSOCKET_RECONNECT_MS: 5000, // 5 segundos
  SESSION_TIMEOUT_MINUTES: 60, // 1 hora
} as const;

// ==================== VALIDAÇÃO ====================
export const VALIDATION = {
  // Email
  EMAIL_REGEX: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,

  // Telefone (Brasil)
  PHONE_REGEX: /^\+?55?\s?\(?[1-9]{2}\)?\s?9?\d{4}-?\d{4}$/,
  PHONE_CLEAN_REGEX: /^\+?[0-9]{10,15}$/,

  // Senha
  MIN_PASSWORD_LENGTH: 8,
  PASSWORD_REGEX: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,

  // Nome
  MIN_NAME_LENGTH: 3,
  MAX_NAME_LENGTH: 100,
  NAME_REGEX: /^[a-zA-ZÀ-ÿ\s]+$/,
} as const;

// ==================== MENSAGENS DE ERRO ====================
export const ERROR_MESSAGES = {
  // Autenticação
  INVALID_CREDENTIALS: 'Email ou senha inválidos',
  SESSION_EXPIRED: 'Sua sessão expirou. Faça login novamente.',
  UNAUTHORIZED: 'Você não tem permissão para acessar este recurso',
  FORBIDDEN: 'Acesso negado',

  // Validação
  INVALID_EMAIL: 'Email inválido',
  INVALID_PHONE: 'Telefone inválido',
  WEAK_PASSWORD:
    'Senha deve ter no mínimo 8 caracteres, incluindo maiúsculas, minúsculas, números e símbolos',
  REQUIRED_FIELD: 'Este campo é obrigatório',

  // Rate Limiting
  TOO_MANY_ATTEMPTS: 'Muitas tentativas. Tente novamente em alguns minutos.',
  RATE_LIMIT_EXCEEDED: 'Limite de requisições excedido. Aguarde um momento.',

  // Rede
  NETWORK_ERROR: 'Erro de conexão. Verifique sua internet.',
  SERVER_ERROR: 'Erro no servidor. Tente novamente mais tarde.',
  TIMEOUT_ERROR: 'Tempo esgotado. Tente novamente.',

  // Chat
  MESSAGE_TOO_LONG: `Mensagem muito longa. Máximo de ${LIMITS.MAX_MESSAGE_LENGTH} caracteres.`,
  CHAT_UNAVAILABLE: 'Chat temporariamente indisponível',

  // Geral
  UNKNOWN_ERROR: 'Ocorreu um erro inesperado',
} as const;

// ==================== MENSAGENS DE SUCESSO ====================
export const SUCCESS_MESSAGES = {
  LOGIN_SUCCESS: 'Login realizado com sucesso',
  LOGOUT_SUCCESS: 'Logout realizado com sucesso',
  REGISTER_SUCCESS: 'Cadastro realizado com sucesso',
  UPDATE_SUCCESS: 'Atualização realizada com sucesso',
  DELETE_SUCCESS: 'Exclusão realizada com sucesso',
  MESSAGE_SENT: 'Mensagem enviada',
  CHAT_ASSUMED: 'Atendimento assumido com sucesso',
  CHAT_RETURNED: 'Conversa devolvida ao bot',
} as const;

// ==================== TYPE HELPERS ====================
export type ChatState = (typeof CHAT_STATES)[keyof typeof CHAT_STATES];
